import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Check, CheckCircle, X, ArrowsClockwise, GearSix, Lightning } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  critical: { dot: 'bg-red-500', bg: 'bg-red-50 border-red-200', label: 'text-red-700', ring: 'ring-red-500' },
  important: { dot: 'bg-amber-500', bg: 'bg-amber-50 border-amber-200', label: 'text-amber-700', ring: 'ring-amber-500' },
  info: { dot: 'bg-zinc-400', bg: 'bg-zinc-50 border-zinc-200', label: 'text-zinc-600', ring: 'ring-zinc-400' },
};

const INTERVAL_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hr' },
  { value: 120, label: '2 hr' },
  { value: 360, label: '6 hr' },
  { value: 720, label: '12 hr' },
  { value: 1440, label: '24 hr' },
];

function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cronStatus, setCronStatus] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role && ['admin', 'super_admin'].includes(user.role);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/unread-count`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch (e) { /* silent */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications?limit=30`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when opened
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = async (n) => {
    // Mark as read
    await fetch(`${BACKEND_URL}/api/notifications/${n.id}/read`, {
      method: 'PUT', credentials: 'include',
    });
    setOpen(false);
    fetchUnreadCount();
    if (n.action_link) {
      navigate(n.action_link);
    } else if (n.project_id) {
      navigate(`/projects/${n.project_id}`);
    } else if (n.entity_type === 'indent') {
      navigate('/procurement');
    } else {
      navigate('/action-center');
    }
  };

  const handleAct = async (e, n) => {
    e.stopPropagation();
    await fetch(`${BACKEND_URL}/api/notifications/${n.id}/acted`, {
      method: 'PUT', credentials: 'include',
    });
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    fetchUnreadCount();
  };

  const handleMarkAllRead = async () => {
    await fetch(`${BACKEND_URL}/api/notifications/mark-all-read`, {
      method: 'PUT', credentials: 'include',
    });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await fetch(`${BACKEND_URL}/api/notifications/scan`, {
        method: 'POST', credentials: 'include',
      });
      await fetch(`${BACKEND_URL}/api/notifications/process-escalations`, {
        method: 'POST', credentials: 'include',
      });
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (e) { /* silent */ }
    setScanning(false);
  };

  const fetchCronStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/cron-status`, { credentials: 'include' });
      if (res.ok) setCronStatus(await res.json());
    } catch (e) { /* silent */ }
  }, [isAdmin]);

  const updateConfig = async (updates) => {
    setSavingConfig(true);
    try {
      await fetch(`${BACKEND_URL}/api/notifications/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      await fetchCronStatus();
    } catch (e) { /* silent */ }
    setSavingConfig(false);
  };

  // Fetch cron status when settings panel opens
  useEffect(() => {
    if (showSettings) fetchCronStatus();
  }, [showSettings, fetchCronStatus]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="relative p-1.5 rounded-sm text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={18} weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[480px] bg-white rounded-lg shadow-2xl border border-zinc-200 overflow-hidden z-[100]"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50">
            <span className="text-sm font-semibold text-zinc-800">Notifications</span>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button
                  onClick={() => setShowSettings(prev => !prev)}
                  className={`text-[11px] font-medium flex items-center gap-0.5 ${showSettings ? 'text-zinc-800' : 'text-zinc-400 hover:text-zinc-600'}`}
                  data-testid="notification-settings-btn"
                  title="Cron settings"
                >
                  <GearSix size={13} />
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={handleScan}
                  disabled={scanning}
                  className="text-[11px] text-zinc-500 hover:text-zinc-800 font-medium flex items-center gap-1 disabled:opacity-50"
                  data-testid="notification-scan-btn"
                  title="Scan for pending actions"
                >
                  <ArrowsClockwise size={12} className={scanning ? 'animate-spin' : ''} />
                  {scanning ? 'Checking' : 'Check Issues'}
                </button>
              )}
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-600">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Settings Panel (Admin Only) */}
          {showSettings && isAdmin && cronStatus && (
            <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/50 space-y-2.5" data-testid="notification-settings-panel">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-zinc-700">Auto-Scan Engine</span>
                <button
                  onClick={() => updateConfig({ cron_enabled: !cronStatus.cron_enabled })}
                  disabled={savingConfig}
                  className={`relative w-8 h-[18px] rounded-full transition-colors ${cronStatus.cron_enabled ? 'bg-green-500' : 'bg-zinc-300'}`}
                  data-testid="cron-toggle"
                >
                  <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${cronStatus.cron_enabled ? 'translate-x-[15px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="text-[10px] text-zinc-500 block mb-0.5">Scan interval</label>
                  <select
                    value={cronStatus.scan_interval_minutes}
                    onChange={(e) => updateConfig({ scan_interval_minutes: Number(e.target.value) })}
                    disabled={savingConfig}
                    className="w-full text-[11px] border border-zinc-200 rounded px-1.5 py-1 bg-white"
                    data-testid="scan-interval-select"
                  >
                    {INTERVAL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-zinc-500 block mb-0.5">Escalation interval</label>
                  <select
                    value={cronStatus.escalation_interval_minutes}
                    onChange={(e) => updateConfig({ escalation_interval_minutes: Number(e.target.value) })}
                    disabled={savingConfig}
                    className="w-full text-[11px] border border-zinc-200 rounded px-1.5 py-1 bg-white"
                    data-testid="escalation-interval-select"
                  >
                    {INTERVAL_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-zinc-400">
                <span className="flex items-center gap-1">
                  <Lightning size={10} className={cronStatus.running ? 'text-green-500' : 'text-red-400'} weight="fill" />
                  {cronStatus.running ? 'Engine running' : 'Engine stopped'}
                </span>
                <span>Last scan: {timeAgo(cronStatus.last_scan_at)}</span>
              </div>
            </div>
          )}

          {/* List */}
          <div className="overflow-y-auto max-h-[420px]">
            {loading && notifications.length === 0 ? (
              <div className="p-6 text-center text-xs text-zinc-400">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center" data-testid="notification-empty">
                <CheckCircle size={32} className="mx-auto text-green-400 mb-2" />
                <p className="text-sm text-zinc-500 font-medium">All clear</p>
                <p className="text-xs text-zinc-400 mt-0.5">No pending actions</p>
              </div>
            ) : (
              notifications.map((n) => {
                const cfg = PRIORITY_CONFIG[n.priority] || PRIORITY_CONFIG.info;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-zinc-50 cursor-pointer transition-colors
                      ${n.is_read ? 'bg-white hover:bg-zinc-50' : `${cfg.bg} hover:opacity-90`}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.is_read ? 'bg-transparent' : cfg.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs font-semibold truncate ${n.is_read ? 'text-zinc-500' : 'text-zinc-800'}`}>
                          {n.escalation_level > 0 && <span className="text-red-600 mr-1">ESC</span>}
                          {n.title}
                        </p>
                        <button
                          onClick={(e) => handleAct(e, n)}
                          title="Dismiss"
                          className="shrink-0 p-0.5 text-zinc-400 hover:text-green-600 rounded"
                          data-testid={`notification-dismiss-${n.id}`}
                        >
                          <Check size={14} />
                        </button>
                      </div>
                      <p className={`text-[11px] mt-0.5 line-clamp-2 ${n.is_read ? 'text-zinc-400' : 'text-zinc-600'}`}>
                        {n.message}
                      </p>
                      <span className="text-[10px] text-zinc-400 mt-1 block">{timeAgo(n.created_at)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
